import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Chip,
    Divider,
    Grid,
    Paper,
    IconButton,
    Tooltip,
    TextField
} from '@mui/material';
import {
    Sync as SyncIcon,
    CalendarToday as CalendarIcon,
    MedicalServices as MedicalServicesIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import api from '../services/api';

interface ExecutionUnit {
    id: string;
    code: string;
    name: string;
    description: string;
    diariesCount?: number;
    lastSync?: string;
}

const DiarySyncPage: React.FC = () => {
    const [executionUnits, setExecutionUnits] = useState<ExecutionUnit[]>([]);
    const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [syncErrors, setSyncErrors] = useState<string[]>([]);
    
    // Stati per sync per ID
    const [diaryId, setDiaryId] = useState<string>('');
    const [syncingById, setSyncingById] = useState(false);

    useEffect(() => {
        loadExecutionUnits();
    }, []);

    const loadExecutionUnits = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/integration/execution-units');
            if (response.data.success && response.data.data) {
                setExecutionUnits(response.data.data);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore nel caricamento delle unità eroganti');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (unitId: string) => () => {
        const currentIndex = selectedUnits.indexOf(unitId);
        const newChecked = [...selectedUnits];

        if (currentIndex === -1) {
            newChecked.push(unitId);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setSelectedUnits(newChecked);
    };

    const handleSelectAll = () => {
        if (selectedUnits.length === executionUnits.length) {
            setSelectedUnits([]);
        } else {
            setSelectedUnits(executionUnits.map(unit => unit.id));
        }
    };

    const syncDiaries = async () => {
        if (selectedUnits.length === 0) {
            setError('Seleziona almeno un\'unità erogante');
            return;
        }

        setSyncing(true);
        setError(null);
        setSuccess(null);
        setSyncErrors([]);

        try {
            // Ottieni i codici delle unità selezionate
            const executionUnitCodes = executionUnits
                .filter(unit => selectedUnits.includes(unit.id))
                .map(unit => unit.code || unit.id);

            const response = await api.post('/recup-sync/sync/diaries', {
                executionUnitCodes
            });

            const data = response.data;
            
            if (data.errors > 0 && data.errorDetails) {
                setSyncErrors(data.errorDetails);
            }
            
            if (data.created > 0 || data.updated > 0) {
                setSuccess(
                    `Sincronizzazione completata! ` +
                    `${data.created || 0} agende create, ` +
                    `${data.updated || 0} aggiornate` +
                    (data.errors > 0 ? `, ${data.errors} errori` : '')
                );
            } else if (data.errors > 0) {
                setError(`Sincronizzazione fallita: ${data.errors} errori. Vedi i dettagli sotto.`);
            }
            
            // Ricarica le unità per aggiornare i contatori
            loadExecutionUnits();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore durante la sincronizzazione delle agende');
        } finally {
            setSyncing(false);
        }
    };

    const syncDiaryById = async () => {
        if (!diaryId.trim()) {
            setError('Inserire un ID agenda valido');
            return;
        }

        setSyncingById(true);
        setError(null);
        setSuccess(null);
        setSyncErrors([]);

        try {
            const response = await api.post('/recup-sync/diaries/by-id', {
                diaryId: diaryId.trim()
            });

            if (response.data.success) {
                setSuccess(`Agenda ${diaryId} sincronizzata con successo!`);
                // Ricarica le unità per aggiornare i contatori
                loadExecutionUnits();
            } else {
                setError(response.data.error || 'Errore durante la sincronizzazione');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Errore durante la sincronizzazione dell\'agenda');
        } finally {
            setSyncingById(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" component="h2">
                            <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Sincronizzazione Agende
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadExecutionUnits}
                                disabled={loading || syncing}
                            >
                                Ricarica
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SyncIcon />}
                                onClick={syncDiaries}
                                disabled={syncing || selectedUnits.length === 0}
                            >
                                Sincronizza Agende ({selectedUnits.length})
                            </Button>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Sezione Sincronizzazione per ID */}
                    <Box sx={{ mb: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="h6" gutterBottom>
                            Sincronizza Agenda per ID
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Sincronizza una singola agenda specifica inserendo il suo ID RECUP
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <TextField
                                label="ID Agenda"
                                placeholder="es. 124425"
                                value={diaryId}
                                onChange={(e) => setDiaryId(e.target.value)}
                                size="small"
                                type="number"
                                disabled={syncingById || syncing}
                                sx={{ width: '200px' }}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={syncingById ? <CircularProgress size={20} /> : <SyncIcon />}
                                onClick={syncDiaryById}
                                disabled={syncingById || syncing || !diaryId.trim()}
                            >
                                {syncingById ? 'Sincronizzazione...' : 'Sincronizza per ID'}
                            </Button>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}
                    
                    {syncErrors.length > 0 && (
                        <Alert 
                            severity="warning" 
                            sx={{ mb: 2 }} 
                            onClose={() => setSyncErrors([])}
                        >
                            <Typography variant="subtitle2" gutterBottom>
                                Dettagli errori di sincronizzazione:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                {syncErrors.map((errorMsg, index) => (
                                    <li key={index} style={{ fontSize: '0.875rem' }}>
                                        {errorMsg}
                                    </li>
                                ))}
                            </Box>
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">
                                        Unità Eroganti Disponibili
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={handleSelectAll}
                                        disabled={loading || syncing}
                                    >
                                        {selectedUnits.length === executionUnits.length ? 'Deseleziona tutto' : 'Seleziona tutto'}
                                    </Button>
                                </Box>

                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : executionUnits.length === 0 ? (
                                    <Alert severity="info">
                                        Nessuna unità erogante trovata. 
                                        Sincronizza prima le unità dalla Struttura Gerarchica.
                                    </Alert>
                                ) : (
                                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                                        {executionUnits.map((unit) => {
                                            const labelId = `checkbox-list-label-${unit.id}`;
                                            const isSelected = selectedUnits.indexOf(unit.id) !== -1;

                                            return (
                                                <ListItem
                                                    key={unit.id}
                                                    disablePadding
                                                    secondaryAction={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {unit.diariesCount !== undefined && (
                                                                <Chip
                                                                    label={`${unit.diariesCount} agende`}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {unit.lastSync && (
                                                                <Tooltip title={`Ultimo sync: ${new Date(unit.lastSync).toLocaleString()}`}>
                                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    }
                                                >
                                                    <ListItemButton 
                                                        role={undefined} 
                                                        onClick={handleToggle(unit.id)} 
                                                        dense
                                                        disabled={syncing}
                                                    >
                                                        <ListItemIcon>
                                                            <Checkbox
                                                                edge="start"
                                                                checked={isSelected}
                                                                tabIndex={-1}
                                                                disableRipple
                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemIcon>
                                                            <MedicalServicesIcon color="primary" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            id={labelId}
                                                            primary={unit.name || unit.description}
                                                            secondary={`Codice: ${unit.code}`}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {syncing && (
                        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress size={24} sx={{ mr: 2 }} />
                            <Typography>Sincronizzazione in corso...</Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default DiarySyncPage;